import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import { Panel } from '../components/Panel.jsx';
import { Resource } from '../components/StateViews.jsx';
import { Tag } from '../components/StatusPill.jsx';
import { formatRelative, formatTimestamp } from '../lib/format.js';

const LIMITS = [25, 50, 100, 200];
const DEFAULT_LIMIT = 50;

export default function Audit() {
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const load = useCallback((signal) => endpoints.audit(limit, signal), [limit]);
  const state = useResource(load, [limit]);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Registry"
        title="Audit log"
        lede="Every privileged action liftedctl has taken, newest first."
        actions={
          <>
            <label className="select" htmlFor="audit-limit">
              <span className="select__label">Rows</span>
              <select
                id="audit-limit"
                className="select__input mono"
                value={limit}
                onChange={(event) => setLimit(Number(event.target.value))}
              >
                {LIMITS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn" onClick={state.refresh}>
              Refresh
            </button>
          </>
        }
      />

      <Resource
        state={state}
        loadingLabel="Loading audit entries…"
        loadingRows={6}
        errorTitle="Could not load the audit log"
        isEmpty={(data) => !Array.isArray(data) || data.length === 0}
        emptyTitle="No audit entries"
        emptyBody="Nothing has been recorded yet. Entries appear as soon as liftedctl performs an action."
      >
        {(entries) => (
          <Panel title="Recent activity" subtitle={`${entries.length} entries`}>
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Actor</th>
                    <th scope="col">Action</th>
                    <th scope="col">Target</th>
                    <th scope="col">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr key={`${entry.ts}-${entry.action}-${entry.target}-${index}`}>
                      <th scope="row" className="mono nowrap">
                        {formatTimestamp(entry.ts)}
                        <span className="table__sub">{formatRelative(entry.ts)}</span>
                      </th>
                      <td className="mono">{entry.actor || <span className="is-muted">-</span>}</td>
                      <td>
                        <Tag mono>{entry.action || 'unknown'}</Tag>
                      </td>
                      <td className="mono">
                        {entry.target ? (
                          <Link className="table__link" to={`/sites/${encodeURIComponent(entry.target)}`}>
                            {entry.target}
                          </Link>
                        ) : (
                          <span className="is-muted">-</span>
                        )}
                      </td>
                      <td className="mono detail-cell">
                        {entry.detail || <span className="is-muted">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}
      </Resource>
    </div>
  );
}
